"use client";

import clsx from "clsx";
import {TFunction} from "i18next";
import {Trans} from "react-i18next";
import AudioPreviewButton from "@/app/(customers)/settings/AudioPreviewButton";
import MultiAudioPreviewButton from "@/app/(customers)/settings/MultiAudioPreviewButton";
import {ICON_SIZES} from "@/components/library/Icon";
import {Skeleton} from "@/components/ui/skeleton";
import {SupportedLanguages} from "@/enums/internationalization";
import {VoiceProvider} from "@/enums/agencies";
import {VoiceOption} from "@/types/agencies";
import {formatSpokenLanguages, formatVoiceSubtitle} from "@/utils/scripts/voiceFormat";
import {buildVoiceSampleUrl} from "@/utils/scripts/voiceSampleUrl";

type TranslateFn = TFunction;

interface LoadingVoiceCardProps {
  loading: true;
}

interface ReadyVoiceCardProps {
  loading?: false;
  voice: VoiceOption;
  isSelected: boolean;
  onSelect: (voiceId: string) => void;
  changeMarker: string;
  setPlaying: (audioId: string) => void;
  translateCommon: TranslateFn;
  translateSettings: TranslateFn;
  orgId?: string;
}

type VoiceCardProps = LoadingVoiceCardProps | ReadyVoiceCardProps;

function VoiceCardSkeleton() {
  return (
    <div className="space-y-2.5 p-4 border border-border/60 rounded-xl overflow-hidden bg-background">
      <Skeleton className="w-24 max-w-full h-4" />
      <Skeleton className="w-36 max-w-full h-3" />
      <Skeleton className="w-28 max-w-full h-3 mt-3" />
    </div>
  );
}

function ReadyVoiceCard({
  voice,
  isSelected,
  onSelect,
  changeMarker,
  setPlaying,
  translateCommon,
  translateSettings,
  orgId,
}: Readonly<ReadyVoiceCardProps>) {
  const showMultiPreview = voice.supportedLanguages.length > 1;

  const previewUrls = voice.supportedLanguages.reduce(
    (acc, lang) => {
      acc[lang] = buildVoiceSampleUrl(voice.id, lang, {
        provider: voice.provider,
        orgId,
        isOrganizationVoice: voice.isOrganizationVoice,
      });
      return acc;
    },
    {} as Partial<Record<SupportedLanguages, string>>
  );

  const previewUrl = buildVoiceSampleUrl(voice.id, voice.supportedLanguages[0], {
    provider: voice.provider,
    orgId,
    isOrganizationVoice: voice.isOrganizationVoice,
  });

  const subtitle = formatVoiceSubtitle(voice, translateSettings);
  const spokenLanguages = formatSpokenLanguages(voice.supportedLanguages, translateCommon);

  return (
    <div className="relative group h-full">
      <button
        type="button"
        onClick={() => onSelect(voice.id)}
        className={clsx(
          "p-4 rounded-xl w-full h-full text-left transition-all cursor-pointer",
          "appearance-none font-inherit border-2",
          isSelected
            ? "border-primary ring-1 ring-primary/20 bg-primary/[0.03] shadow-sm"
            : "border-border/70 hover:border-border hover:bg-muted/30 bg-background"
        )}
        aria-label={translateCommon("selectVoiceNamed", {name: voice.name})}
        aria-pressed={isSelected}
      >
        <div className="flex flex-col h-full">
          <span className="font-semibold text-foreground text-base leading-snug pr-8">
            {voice.name}
          </span>
          {voice.provider === VoiceProvider.Gemini && (
            <span className="mt-2 w-fit rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
              {translateSettings("voiceDirectionAvailable")}
            </span>
          )}
          {subtitle && (
            <span className="text-muted-foreground text-xs mt-2">{subtitle}</span>
          )}
          {voice.provider === VoiceProvider.Gemini && voice.description && (
            <p className="mt-2 line-clamp-2 text-xs leading-5 text-muted-foreground">{voice.description}</p>
          )}
          {spokenLanguages.display && (
            <p className="text-xs text-muted-foreground mt-3">
              <Trans
                t={translateCommon}
                i18nKey="speaksLanguages"
                values={{languages: spokenLanguages.display}}
                components={{
                  bold: (
                    <span
                      key="bold"
                      className="font-semibold text-foreground whitespace-nowrap"
                    />
                  ),
                }}
              />
            </p>
          )}
        </div>
      </button>

      <div className="absolute top-2 right-3 flex items-center">
        {showMultiPreview ? (
          <MultiAudioPreviewButton
            supportedLanguages={voice.supportedLanguages}
            setPlaying={setPlaying}
            changeMarker={changeMarker}
            url={previewUrls}
            size={ICON_SIZES.XL}
          />
        ) : (
          <AudioPreviewButton
            setPlaying={setPlaying}
            changeMarker={changeMarker}
            url={previewUrl}
            size={ICON_SIZES.XL}
          />
        )}
      </div>
    </div>
  );
}

export default function VoiceCard(props: Readonly<VoiceCardProps>) {
  if (props.loading) {
    return <VoiceCardSkeleton />;
  }

  return <ReadyVoiceCard {...props} />;
}
